import React, { useMemo } from "react";
import { useCartStore } from "../store/cart.store";
import { Card } from "../components/common/Card";
import { Button } from "../components/common/Button";
import { useNavigate } from "react-router-dom";
import CircleLogo from "../components/CircleLogo"; 
import { useAuth } from "../context/AuthProvider";
import { toast } from "react-hot-toast";
import { parseAndValidatePrice } from '../utils/priceValidator';
import { useDeliveryFeeConfig } from '../hooks/useDeliveryFeeConfig';
import { useCommunityOrder } from '../hooks/useCommunityOrder';
import { calculateCustomerPlatformFee } from '../services/rateCard.service';

// ✅ HELPER: Safely extract values (variant, size, addons) from Array OR Object
const getCustomization = (customizations: any, key: string): string | null => {
  if (!customizations) return null;

  // 1. Handle New Array Structure
  if (Array.isArray(customizations)) {
     // Special handling for Addons (might be multiple)
     if (key === 'addons') {
        const addons = customizations
            .filter((c: any) => c.type === 'addon' || c.label.toLowerCase() === 'addons')
            .map((c: any) => c.value);
        return addons.length > 0 ? addons.join(", ") : null;
     }

     // For variant/size, find first match
     const found = customizations.find((c: any) => 
        c.label?.toLowerCase() === key.toLowerCase() || 
        c.type?.toLowerCase() === key.toLowerCase()
     );
     return found ? found.value : null;
  }

  // 2. Handle Legacy Object Structure
  const val = customizations[key];
  if (key === 'addons' && Array.isArray(val)) {
     return val.length > 0 ? val.join(", ") : null;
  }
  return val || null;
};

const Cart: React.FC = () => {
  const items = useCartStore((state) => state.items);
  const totalPrice = useCartStore((state) => state.totalPrice); 
  const updateQuantity = useCartStore((state) => state.updateQuantity);
  const removeItem = useCartStore((state) => state.removeItem);
  const clearCart = useCartStore((state) => state.clearCart);
  const kitchenCount = useCartStore((state) => state.kitchenCount);
  const navigate = useNavigate();
  const numKitchens = kitchenCount();

  // Group line items under kitchen headers when multiple kitchens
  const itemsByKitchen = useMemo(() => {
    const groups: { kitchenId: string; kitchenName: string; items: typeof items }[] = [];
    const index = new Map<string, number>();
    for (const item of items) {
      const kId = item.kitchenId || "unknown";
      if (!index.has(kId)) {
        index.set(kId, groups.length);
        groups.push({
          kitchenId: kId,
          kitchenName: item.kitchenName || "Unknown Kitchen",
          items: [],
        });
      }
      groups[index.get(kId)!].items.push(item);
    }
    return groups;
  }, [items]);
  const { user, loading: authLoading } = useAuth(); 
  
  const subtotal = totalPrice();
  const itemCount = items.reduce((sum, item) => sum + (item.quantity || 1), 0);
  const community = useCommunityOrder();

  const { 
    deliveryFee, 
    threshold, 
    loading: feeLoading, 
    calculationMethod,
    getDeliveryFee,
    rateConfig 
  } = useDeliveryFeeConfig(subtotal, itemCount, community.isCommunity);

  const handleCheckout = () => {
    if (authLoading || feeLoading) return;

    if (!user) {
      toast.error("Please log in to proceed to checkout.");
      navigate("/login");
      return;
    }
    
    navigate("/checkout");
  };

  if (items.length === 0) {
    return (
      <div className="max-w-2xl mx-auto py-16 px-4 text-center">
        <div className="mb-6 text-6xl">🛒</div>
        <h2 className="text-2xl font-bold text-gray-800 mb-2">Your cart is empty</h2>
        <p className="text-gray-500 mb-8">Looks like you haven't added anything yet.</p>
        <Button onClick={() => navigate("/")}>Start Ordering</Button>
      </div>
    );
  }

  const platformFee = rateConfig
    ? calculateCustomerPlatformFee(subtotal, rateConfig)
    : 0;
  const finalTotal = subtotal + deliveryFee + platformFee;
  const isBelowThreshold = subtotal < threshold;
  const amountToFreeFee = threshold - subtotal;

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold text-gray-900">Your Cart</h1>
          {numKitchens > 1 && (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-orange-100 text-orange-700">
              {numKitchens} kitchens
            </span>
          )}
        </div>
        <button 
          onClick={clearCart}
          className="text-sm text-red-500 hover:text-red-700 font-medium underline"
        >
          Clear Cart
        </button>
      </div>

      <div className="space-y-6">
        {itemsByKitchen.map((group) => (
          <div key={group.kitchenId} className="space-y-3">
            {numKitchens > 1 && (
              <div className="flex items-center gap-2 px-1">
                <span className="text-sm font-bold text-gray-800">{group.kitchenName}</span>
                <span className="text-xs text-gray-400">
                  {group.items.reduce((s, i) => s + i.quantity, 0)} item(s)
                </span>
              </div>
            )}
            {group.items.map((item) => {
          const price = parseAndValidatePrice(item.price);
          const isInvalidPrice = price === 0 && item.price !== 0;
          const itemTotal = price * item.quantity;

          // ✅ Prepare customization values using helper
          const variant = getCustomization(item.customizations, 'variant');
          const size = getCustomization(item.customizations, 'size');
          const addons = getCustomization(item.customizations, 'addons');

          return (
            <Card key={item.id} className="p-4">
              <div className="flex gap-4">
                <div className="w-20 h-20 shrink-0">
                   <CircleLogo 
                      src={item.photoUrl || "/food-hero.svg"} 
                      size={80} 
                      borderColor="transparent"
                      alt={item.name}
                      className="rounded-lg" 
                   />
                </div>

                <div className="flex-1 flex flex-col justify-between">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-bold text-gray-800 text-lg">{item.name}</h3>
                      {numKitchens <= 1 && (
                        <p className="text-sm text-gray-500">{item.kitchenName}</p>
                      )}
                      
                      {/* ✅ FIXED: Use extracted variables instead of direct access */}
                      <div className="mt-1 text-xs text-gray-600 space-y-0.5">
                        {variant && <p>• {variant}</p>}
                        {size && <p>• {size}</p>}
                        {addons && <p>• Extras: {addons}</p>}
                      </div>
                    </div>
                    <p className={`font-bold ${isInvalidPrice ? 'text-red-500' : 'text-orange-600'}`}>
                      {isInvalidPrice 
                        ? '⚠️ Price unavailable' 
                        : `₹${itemTotal.toFixed(2)}`
                      }
                    </p>
                  </div>

                  <div className="flex justify-end items-center gap-4 mt-2">
                    <div className="flex items-center border rounded-lg bg-gray-50">
                      <button
                        className="px-3 py-1 text-gray-600 hover:bg-gray-200 rounded-l-lg font-bold transition"
                        onClick={() => updateQuantity(item.id, item.quantity - 1)}
                      >
                        -
                      </button>
                      <span className="px-2 font-medium text-gray-800 w-6 text-center">
                        {item.quantity}
                      </span>
                      <button
                        className="px-3 py-1 text-gray-600 hover:bg-gray-200 rounded-r-lg font-bold transition"
                        onClick={() => updateQuantity(item.id, item.quantity + 1)}
                      >
                        +
                      </button>
                    </div>
                    <button 
                      className="text-xs text-red-500 hover:text-red-700 font-medium underline"
                      onClick={() => removeItem(item.id)}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              </div>
            </Card>
          );
            })}
          </div>
        ))}
      </div>

      <div className="mt-8 bg-white p-6 rounded-xl shadow-sm border border-gray-100">
        <div className="flex justify-between items-center mb-2">
          <span className="text-gray-600">Subtotal</span>
          <span className="font-semibold">₹{subtotal.toFixed(2)}</span>
        </div>
        
        <div className="flex justify-between items-center mb-4 p-3 bg-gradient-to-r from-blue-50 to-orange-50 rounded-lg">
          <div>
            <span className="text-gray-700 font-medium">Delivery Fee</span>
            {feeLoading && <span className="text-xs text-gray-400 ml-2">(updating...)</span>}
            
            {isBelowThreshold && (
              <div className="text-xs text-orange-600 font-semibold mt-1 flex items-center gap-1">
                🎁 Add ₹{amountToFreeFee.toFixed(2)} for lower delivery fee!
              </div>
            )}
            
            {!isBelowThreshold && rateConfig && (
              <div className="text-xs text-green-600 font-semibold mt-1 flex items-center gap-1">
                ✨ Great! You got reduced delivery fee
              </div>
            )}
          </div>
          <div className="text-right">
            <span className="font-bold text-lg block text-orange-600">₹{deliveryFee.toFixed(2)}</span>
            {community.isCommunity && (
              <span className="text-[11px] text-green-700 font-medium block mt-0.5">
                Community rate · {community.communityName || "Bren Northern Lights"}
              </span>
            )}
            {!community.isCommunity && calculationMethod && (
              <span className="text-[11px] text-gray-400 block mt-0.5">{calculationMethod}</span>
            )}
            {rateConfig && (
              <span className="text-xs text-gray-500">
                {subtotal >= threshold 
                  ? `(Above ₹${threshold})` 
                  : `(Below ₹${threshold})`
                }
              </span>
            )}
          </div>
        </div>

        <div className="flex justify-between items-center mb-2">
          <span className="text-gray-600">Platform fee</span>
          <span className="font-semibold">₹{platformFee.toFixed(2)}</span>
        </div>

        <div className="flex justify-between items-center border-t pt-4 mb-6">
          <span className="text-xl font-bold text-gray-900">Total</span>
          <span className="text-xl font-bold text-orange-600">₹{finalTotal.toFixed(2)}</span>
        </div>
        
        <div className="flex gap-4">
           <Button 
             variant="secondary" 
             className="flex-1" 
             onClick={() => navigate("/")}
           >
             Add More Items
           </Button>
           <Button 
             className="flex-[2]" 
             onClick={handleCheckout}
             disabled={authLoading || feeLoading} 
             isLoading={authLoading || feeLoading} 
           >
             Proceed to Checkout
           </Button>
        </div>
      </div>
    </div>
  );
};

export default Cart;